/**
 * CampaignSettings - Payment methods, visibility, email templates, communication config
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  CreditCard, Eye, Mail, MessageSquare, Plus, Trash2, Save,
  Loader2, Smartphone, Globe, Link2, Banknote, Building2, Send, X,
  Users, UserPlus, Shield, Pencil, FileText
} from 'lucide-react';
import api from '@/lib/api';
import DonationCMSTab from './campaigns/DonationCMSTab';

const ICON_MAP = {
  banknotes: Banknote, 'building-library': Building2, smartphone: Smartphone,
  'credit-card': CreditCard, globe: Globe, link: Link2
};

const CampaignSettings = ({ campaign, onClose }) => {
  const queryClient = useQueryClient();
  if (!campaign) return null;

  return (
    <Dialog open={!!campaign} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Campaign Settings - {campaign.name}</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="payment" className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-9">
            <TabsTrigger value="payment" className="text-xs"><CreditCard className="h-3 w-3 mr-1" />Payment</TabsTrigger>
            <TabsTrigger value="cms" className="text-xs" data-testid="campaign-donation-cms-tab"><FileText className="h-3 w-3 mr-1" />Donation Page</TabsTrigger>
            <TabsTrigger value="team" className="text-xs"><Users className="h-3 w-3 mr-1" />Team</TabsTrigger>
            <TabsTrigger value="emailblast" className="text-xs"><Send className="h-3 w-3 mr-1" />Email Donors</TabsTrigger>
            <TabsTrigger value="whatsappblast" className="text-xs" data-testid="campaign-whatsapp-tab"><MessageSquare className="h-3 w-3 mr-1" />WhatsApp</TabsTrigger>
            <TabsTrigger value="updates" className="text-xs"><MessageSquare className="h-3 w-3 mr-1" />Updates</TabsTrigger>
            <TabsTrigger value="visibility" className="text-xs"><Eye className="h-3 w-3 mr-1" />Visibility</TabsTrigger>
            <TabsTrigger value="email" className="text-xs"><Mail className="h-3 w-3 mr-1" />Templates</TabsTrigger>
            <TabsTrigger value="comms" className="text-xs"><Globe className="h-3 w-3 mr-1" />Channels</TabsTrigger>
          </TabsList>

          <TabsContent value="payment"><PaymentMethodsTab campaignId={campaign.id} /></TabsContent>
          <TabsContent value="cms"><DonationCMSTab campaignId={campaign.id} /></TabsContent>
          <TabsContent value="team"><CampaignTeamTab campaignId={campaign.id} /></TabsContent>
          <TabsContent value="emailblast"><EmailBlastTab campaignId={campaign.id} campaign={campaign} /></TabsContent>
          <TabsContent value="whatsappblast"><WhatsAppBlastTab campaignId={campaign.id} campaign={campaign} /></TabsContent>
          <TabsContent value="updates"><UpdatesTab campaignId={campaign.id} campaign={campaign} /></TabsContent>
          <TabsContent value="visibility"><VisibilityTab campaignId={campaign.id} campaign={campaign} /></TabsContent>
          <TabsContent value="email"><EmailTemplatesTab campaignId={campaign.id} campaign={campaign} /></TabsContent>
          <TabsContent value="comms"><CommunicationTab campaignId={campaign.id} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// ============== PAYMENT METHODS TAB ==============
const PaymentMethodsTab = ({ campaignId }) => {
  const queryClient = useQueryClient();
  const [methods, setMethods] = useState([]);
  const [customName, setCustomName] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['campaign-payment-methods', campaignId],
    queryFn: () => api.get(`/campaigns/admin/${campaignId}/payment-methods`).then(r => r.data),
  });

  useEffect(() => {
    if (data?.payment_methods) setMethods(data.payment_methods);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => api.put(`/campaigns/admin/${campaignId}/payment-methods`, { payment_methods: methods }),
    onSuccess: () => {
      toast.success('Payment methods updated');
      queryClient.invalidateQueries({ queryKey: ['campaign-payment-methods', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign'] });
    },
    onError: () => toast.error('Failed to update')
  });

  const toggleMethod = (id) => {
    setMethods(prev => prev.map(m => m.id === id ? { ...m, enabled: !m.enabled } : m));
  };

  const updateField = (id, field, value) => {
    setMethods(prev => prev.map(m => m.id === id ? { ...m, [field]: value } : m));
  };

  const addCustomMethod = () => {
    if (!customName.trim()) return;
    const id = customName.toLowerCase().replace(/\s+/g, '_');
    setMethods(prev => [...prev, {
      id, name: customName, enabled: true, type: 'custom',
      instructions: '', icon: 'link'
    }]);
    setCustomName('');
  };

  const removeMethod = (id) => {
    setMethods(prev => prev.filter(m => m.id !== id));
  };

  if (isLoading) return <div className="p-8 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4 pt-4" data-testid="payment-methods-tab">
      <p className="text-sm text-slate-500">Toggle payment methods that supporters can use when making pledges.</p>
      <div className="space-y-3">
        {methods.map(method => {
          const IconComp = ICON_MAP[method.icon] || CreditCard;
          return (
            <Card key={method.id} className={`transition-all ${method.enabled ? 'border-emerald-200 bg-emerald-50/30' : 'opacity-60'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <IconComp className="h-5 w-5 text-slate-600" />
                    <span className="font-medium">{method.name}</span>
                    <span className="text-xs px-2 py-0.5 bg-slate-100 rounded text-slate-500">{method.type}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {method.type === 'custom' && (
                      <Button variant="ghost" size="sm" onClick={() => removeMethod(method.id)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                    <Switch checked={method.enabled} onCheckedChange={() => toggleMethod(method.id)} data-testid={`toggle-${method.id}`} />
                  </div>
                </div>
                {method.enabled && (
                  <div className="mt-3 space-y-2 pl-8">
                    <div>
                      <Label className="text-xs">Instructions for supporters</Label>
                      <Textarea value={method.instructions || ''} onChange={e => updateField(method.id, 'instructions', e.target.value)} placeholder="How to pay using this method..." className="text-sm min-h-[60px]" />
                    </div>
                    {method.type === 'offline' && method.id === 'bank_transfer' && (
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">Bank Name</Label><Input value={method.bank_name || ''} onChange={e => updateField(method.id, 'bank_name', e.target.value)} className="text-sm" /></div>
                        <div><Label className="text-xs">Account Name</Label><Input value={method.account_name || ''} onChange={e => updateField(method.id, 'account_name', e.target.value)} className="text-sm" /></div>
                        <div><Label className="text-xs">Account Number</Label><Input value={method.account_number || ''} onChange={e => updateField(method.id, 'account_number', e.target.value)} className="text-sm" /></div>
                        <div><Label className="text-xs">Branch/SWIFT</Label><Input value={method.swift_code || ''} onChange={e => updateField(method.id, 'swift_code', e.target.value)} className="text-sm" /></div>
                      </div>
                    )}
                    {method.id === 'ecocash' && (
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">Merchant Code</Label><Input value={method.merchant_code || ''} onChange={e => updateField(method.id, 'merchant_code', e.target.value)} className="text-sm" /></div>
                        <div><Label className="text-xs">Merchant Name</Label><Input value={method.merchant_name || ''} onChange={e => updateField(method.id, 'merchant_name', e.target.value)} className="text-sm" /></div>
                      </div>
                    )}
                    {method.id === 'innbucks' && (
                      <div><Label className="text-xs">Merchant Code</Label><Input value={method.merchant_code || ''} onChange={e => updateField(method.id, 'merchant_code', e.target.value)} className="text-sm" /></div>
                    )}
                    {method.id === 'paypal' && (
                      <div><Label className="text-xs">PayPal Email</Label><Input type="email" value={method.paypal_email || ''} onChange={e => updateField(method.id, 'paypal_email', e.target.value)} className="text-sm" /></div>
                    )}
                    {method.id === 'link' && (
                      <div><Label className="text-xs">Payment URL</Label><Input type="url" value={method.payment_url || ''} onChange={e => updateField(method.id, 'payment_url', e.target.value)} placeholder="https://..." className="text-sm" /></div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Add Custom Method */}
      <Card className="border-dashed">
        <CardContent className="p-4 flex gap-2 items-end">
          <div className="flex-1">
            <Label className="text-xs">Add Custom Payment Method</Label>
            <Input value={customName} onChange={e => setCustomName(e.target.value)} placeholder="e.g. Mukuru, WorldRemit" className="text-sm" />
          </div>
          <Button size="sm" onClick={addCustomMethod} disabled={!customName.trim()}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </CardContent>
      </Card>

      <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} className="w-full" data-testid="save-payment-methods">
        {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
        Save Payment Methods
      </Button>
    </div>
  );
};

// ============== VISIBILITY TAB ==============
const VisibilityTab = ({ campaignId, campaign }) => {
  const queryClient = useQueryClient();
  const [visibility, setVisibility] = useState(campaign.visibility || 'public');
  const [eventId, setEventId] = useState(campaign.event_id || '');
  const [churchId, setChurchId] = useState(campaign.church_id || '');
  const [hideDonorNames, setHideDonorNames] = useState(campaign.hide_donor_names || false);

  const { data: events } = useQuery({
    queryKey: ['events-list'],
    queryFn: () => api.get('/admin/events').then(r => r.data),
  });

  const { data: churches } = useQuery({
    queryKey: ['churches-list'],
    queryFn: () => api.get('/churches').then(r => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: () => api.put(`/campaigns/admin/${campaignId}/visibility`, {
      visibility, event_id: eventId, church_id: churchId
    }),
    onSuccess: () => {
      toast.success('Visibility updated');
      queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] });
    },
    onError: () => toast.error('Failed to update')
  });

  const hideDonorMutation = useMutation({
    mutationFn: (hide) => api.put(`/campaigns/admin/${campaignId}/hide-donor-names`, { hide_donor_names: hide }),
    onSuccess: () => {
      toast.success(hideDonorNames ? 'Donor names now hidden on public page' : 'Donor names now visible');
      queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] });
    },
    onError: () => toast.error('Failed to update donor name setting')
  });

  return (
    <div className="space-y-6 pt-4" data-testid="visibility-tab">
      <p className="text-sm text-slate-500">Control who can see this campaign and its pledges.</p>

      <div className="space-y-3">
        {[
          { value: 'public', label: 'Public', desc: 'Visible to everyone. Anyone can view and pledge.' },
          { value: 'private', label: 'Private', desc: 'Only accessible via direct link. Hidden from public listings.' },
          { value: 'event', label: 'Event-Specific', desc: 'Linked to a specific event. Shows on that event\'s page.' },
          { value: 'church', label: 'Church-Specific', desc: 'Linked to a specific church. Shows on that church\'s page.' },
        ].map(opt => (
          <Card key={opt.value} className={`cursor-pointer transition-all ${visibility === opt.value ? 'border-emerald-500 bg-emerald-50/50' : 'hover:border-slate-300'}`}
            onClick={() => setVisibility(opt.value)}>
            <CardContent className="p-4 flex items-start gap-3">
              <input type="radio" name="visibility" value={opt.value} checked={visibility === opt.value} onChange={() => setVisibility(opt.value)} className="mt-1 accent-emerald-600" />
              <div>
                <p className="font-medium">{opt.label}</p>
                <p className="text-sm text-slate-500">{opt.desc}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {visibility === 'event' && (
        <div>
          <Label>Select Event</Label>
          <select value={eventId} onChange={e => setEventId(e.target.value)} className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm">
            <option value="">-- Select an event --</option>
            {(events || []).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
      )}

      {visibility === 'church' && (
        <div>
          <Label>Select Church</Label>
          <select value={churchId} onChange={e => setChurchId(e.target.value)} className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm">
            <option value="">-- Select a church --</option>
            {(churches || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} className="w-full" data-testid="save-visibility">
        {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
        Save Visibility
      </Button>

      {/* Donor Privacy */}
      <div className="border-t pt-4 mt-4">
        <h3 className="font-medium text-sm mb-3">Donor Privacy</h3>
        <label className="flex items-start gap-3 cursor-pointer p-3 border rounded-lg hover:bg-slate-50 transition-colors" data-testid="hide-campaign-donor-names">
          <input 
            type="checkbox" 
            checked={hideDonorNames} 
            onChange={(e) => {
              setHideDonorNames(e.target.checked);
              hideDonorMutation.mutate(e.target.checked);
            }} 
            className="mt-0.5 rounded accent-red-600" 
          />
          <div>
            <p className="text-sm font-medium">Hide All Donor Names</p>
            <p className="text-xs text-slate-500">Show all donors as "Anonymous" on the public campaign page. Individual donor names remain visible to admins only.</p>
          </div>
        </label>
      </div>
    </div>
  );
};

// ============== EMAIL TEMPLATES TAB ==============
const EmailTemplatesTab = ({ campaignId, campaign }) => {
  const queryClient = useQueryClient();
  const [templates, setTemplates] = useState({});
  const [notifySubject, setNotifySubject] = useState('');
  const [notifyBody, setNotifyBody] = useState('');
  const [sending, setSending] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['campaign-email-templates', campaignId],
    queryFn: () => api.get(`/campaigns/admin/${campaignId}/email-templates`).then(r => r.data),
  });

  useEffect(() => {
    if (data?.email_templates) setTemplates(data.email_templates);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => api.put(`/campaigns/admin/${campaignId}/email-templates`, { email_templates: templates }),
    onSuccess: () => {
      toast.success('Email templates updated');
      queryClient.invalidateQueries({ queryKey: ['campaign-email-templates', campaignId] });
    },
    onError: () => toast.error('Failed to update')
  });

  const sendNotification = async () => {
    if (!notifySubject || !notifyBody) { toast.error('Subject and message required'); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('subject', notifySubject);
      fd.append('message_body', notifyBody);
      const res = await api.post(`/campaigns/admin/${campaignId}/notify-pledgers`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Notification sent to ${res.data.sent_count} pledgers`);
      setNotifySubject('');
      setNotifyBody('');
    } catch {
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const TEMPLATE_LABELS = {
    welcome: 'Welcome Email (sent when someone pledges)',
    payment_received: 'Payment Receipt (sent when payment is recorded)',
    campaign_update: 'Campaign Update (template for bulk notifications)',
    pledge_reminder: 'Pledge Reminder'
  };

  if (isLoading) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-6 pt-4" data-testid="email-templates-tab">
      <p className="text-sm text-slate-500">
        Customize email templates. Use variables: {'{{donor_name}}'}, {'{{amount}}'}, {'{{reference}}'}, {'{{remaining}}'}, {'{{total_paid}}'}, {'{{campaign_name}}'}, {'{{message}}'}.
      </p>

      {Object.entries(templates).map(([key, tmpl]) => (
        <Card key={key}>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm">{TEMPLATE_LABELS[key] || key}</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-2">
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={tmpl.subject} onChange={e => setTemplates(prev => ({...prev, [key]: {...prev[key], subject: e.target.value}}))} className="text-sm" />
            </div>
            <div>
              <Label className="text-xs">Body</Label>
              <Textarea value={tmpl.body} onChange={e => setTemplates(prev => ({...prev, [key]: {...prev[key], body: e.target.value}}))} className="text-sm min-h-[100px]" />
            </div>
          </CardContent>
        </Card>
      ))}

      <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} className="w-full" data-testid="save-email-templates">
        {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
        Save Templates
      </Button>

      {/* Send Notification to Pledgers */}
      <Card className="border-amber-200 bg-amber-50/30">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2"><Send className="h-4 w-4" /> Send Update to All Pledgers</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-3">
          <div>
            <Label className="text-xs">Subject</Label>
            <Input value={notifySubject} onChange={e => setNotifySubject(e.target.value)} placeholder="Campaign update subject..." className="text-sm" />
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea value={notifyBody} onChange={e => setNotifyBody(e.target.value)} placeholder="Write your update message to all pledgers..." className="text-sm min-h-[100px]" />
          </div>
          <Button onClick={sendNotification} disabled={sending || !notifySubject || !notifyBody} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="send-notification">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Send to All Pledgers
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== COMMUNICATION CHANNELS TAB ==============
const CommunicationTab = ({ campaignId }) => {
  const queryClient = useQueryClient();
  const [channels, setChannels] = useState([]);
  const [customName, setCustomName] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['campaign-comms', campaignId],
    queryFn: () => api.get(`/campaigns/admin/${campaignId}/communication-config`).then(r => r.data),
  });

  useEffect(() => {
    if (data?.communication_channels) setChannels(data.communication_channels);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => api.put(`/campaigns/admin/${campaignId}/communication-config`, { communication_channels: channels }),
    onSuccess: () => {
      toast.success('Communication channels updated');
      queryClient.invalidateQueries({ queryKey: ['campaign-comms', campaignId] });
    },
    onError: () => toast.error('Failed to update')
  });

  const toggleChannel = (id) => {
    setChannels(prev => prev.map(c => c.id === id ? {...c, enabled: !c.enabled} : c));
  };

  const updateField = (id, field, value) => {
    setChannels(prev => prev.map(c => c.id === id ? {...c, [field]: value} : c));
  };

  const addCustomChannel = () => {
    if (!customName.trim()) return;
    const id = customName.toLowerCase().replace(/\s+/g, '_');
    setChannels(prev => [...prev, { id, name: customName, enabled: true, type: 'custom', phone_number: '' }]);
    setCustomName('');
  };

  const removeChannel = (id) => {
    setChannels(prev => prev.filter(c => c.id !== id));
  };

  const CHANNEL_ICONS = { email: Mail, whatsapp: MessageSquare, sms: Smartphone };

  if (isLoading) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-4 pt-4" data-testid="communication-tab">
      <p className="text-sm text-slate-500">Configure how you communicate with pledgers. Enable channels to reach supporters through their preferred method.</p>

      <div className="space-y-3">
        {channels.map(ch => {
          const Icon = CHANNEL_ICONS[ch.type] || MessageSquare;
          return (
            <Card key={ch.id} className={`transition-all ${ch.enabled ? 'border-emerald-200 bg-emerald-50/30' : 'opacity-60'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-slate-600" />
                    <span className="font-medium">{ch.name}</span>
                    <span className="text-xs px-2 py-0.5 bg-slate-100 rounded text-slate-500">{ch.type}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {ch.type === 'custom' && (
                      <Button variant="ghost" size="sm" onClick={() => removeChannel(ch.id)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                    <Switch checked={ch.enabled} onCheckedChange={() => toggleChannel(ch.id)} />
                  </div>
                </div>
                {ch.enabled && (ch.type === 'whatsapp' || ch.type === 'sms' || ch.type === 'custom') && (
                  <div className="mt-3 pl-8">
                    <Label className="text-xs">Phone Number</Label>
                    <Input value={ch.phone_number || ''} onChange={e => updateField(ch.id, 'phone_number', e.target.value)} placeholder="+263 77 123 4567" className="text-sm" />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Add Custom Channel */}
      <Card className="border-dashed">
        <CardContent className="p-4 flex gap-2 items-end">
          <div className="flex-1">
            <Label className="text-xs">Add Custom Channel</Label>
            <Input value={customName} onChange={e => setCustomName(e.target.value)} placeholder="e.g. Telegram, Signal" className="text-sm" />
          </div>
          <Button size="sm" onClick={addCustomChannel} disabled={!customName.trim()}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </CardContent>
      </Card>

      <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} className="w-full" data-testid="save-communication">
        {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
        Save Communication Settings
      </Button>
    </div>
  );
};

// ============== UPDATES TAB — Project progress posts ==============
const UpdatesTab = ({ campaignId, campaign }) => {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', image_url: '', tags: '', is_published: true });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = React.useRef(null);

  const resetForm = () => {
    setForm({ title: '', content: '', image_url: '', tags: '', is_published: true });
    setImageFile(null);
    setImagePreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error('Max image size is 5MB'); return; }
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
    setForm(prev => ({ ...prev, image_url: '' }));
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview('');
    setForm(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const { data: updates = [], isLoading } = useQuery({
    queryKey: ['campaign-updates-admin', campaignId],
    queryFn: () => api.get(`/campaigns/admin/${campaignId}/updates`).then(r => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (payload) => {
      const fd = new FormData();
      fd.append('title', payload.title);
      fd.append('content', payload.content);
      if (payload.image_url) fd.append('image_url', payload.image_url);
      if (payload.imageFile) fd.append('image', payload.imageFile);
      if (payload.tags) fd.append('tags', payload.tags);
      fd.append('is_published', String(payload.is_published));
      // Force axios to drop the default JSON Content-Type so the browser can
      // set the proper multipart boundary for FormData.
      return api.post(`/campaigns/admin/${campaignId}/updates`, fd, {
        headers: { 'Content-Type': undefined },
      });
    },
    onSuccess: () => {
      toast.success('Update published');
      queryClient.invalidateQueries({ queryKey: ['campaign-updates-admin', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-updates', campaign?.slug] });
      setShowAdd(false);
      resetForm();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to publish'),
  });

  const uploadImageMutation = useMutation({
    mutationFn: ({ updateId, file }) => {
      const fd = new FormData();
      fd.append('image', file);
      return api.post(`/campaigns/admin/${campaignId}/updates/${updateId}/image`, fd, {
        headers: { 'Content-Type': undefined },
      }).then(r => r.data);
    },
  });

  const editMutation = useMutation({
    mutationFn: async ({ updateId, payload, file }) => {
      const res = await api.put(`/campaigns/admin/${campaignId}/updates/${updateId}`, payload);
      if (file) {
        await uploadImageMutation.mutateAsync({ updateId, file });
      }
      return res;
    },
    onSuccess: () => {
      toast.success('Update saved');
      queryClient.invalidateQueries({ queryKey: ['campaign-updates-admin', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-updates', campaign?.slug] });
      setEditing(null);
      resetForm();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to save'),
  });

  const deleteMutation = useMutation({
    mutationFn: (updateId) => api.delete(`/campaigns/admin/${campaignId}/updates/${updateId}`),
    onSuccess: () => {
      toast.success('Update deleted');
      queryClient.invalidateQueries({ queryKey: ['campaign-updates-admin', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-updates', campaign?.slug] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to delete'),
  });

  if (isLoading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-4 pt-2" data-testid="updates-tab">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-slate-500">
          Share progress with your supporters. Published updates appear on{' '}
          {campaign?.slug ? (
            <a href={`/campaigns/${campaign.slug}/updates`} target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline">
              /campaigns/{campaign.slug}/updates
            </a>
          ) : (
            'the public campaign updates page'
          )}.
        </p>
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid="campaign-add-update-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New Update
        </Button>
      </div>

      {updates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-400 text-sm">
            <MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-50" />
            No updates yet. Post your first progress note to keep donors engaged.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {updates.map(u => (
            <Card key={u.id} data-testid={`update-row-${u.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-slate-900 truncate">{u.title}</p>
                      {u.is_published ? (
                        <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Live</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600">Draft</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      by {u.author || 'Admin'} · {u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost" size="sm" className="h-8 w-8 p-0"
                      onClick={() => editMutation.mutate({ updateId: u.id, payload: { is_published: !u.is_published } })}
                      title={u.is_published ? 'Unpublish' : 'Publish'}
                      data-testid={`update-toggle-${u.id}`}
                    >
                      {u.is_published ? <Eye className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5 opacity-40" />}
                    </Button>
                    <Button
                      variant="ghost" size="sm" className="h-8 w-8 p-0"
                      onClick={() => {
                        setEditing(u);
                        setForm({ title: u.title, content: u.content, image_url: u.image_url || '', tags: (u.tags || []).join(','), is_published: u.is_published });
                        setImageFile(null);
                        setImagePreview('');
                      }}
                      title="Edit"
                      data-testid={`update-edit-${u.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600"
                      onClick={() => {
                        if (window.confirm(`Delete "${u.title}"? This can't be undone.`)) {
                          deleteMutation.mutate(u.id);
                        }
                      }}
                      title="Delete"
                      data-testid={`update-delete-${u.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-3">{u.content}</p>
                {u.tags && u.tags.length > 0 && (
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    {u.tags.map(t => (
                      <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t}</span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={showAdd || !!editing} onOpenChange={(open) => { if (!open) { setShowAdd(false); setEditing(null); resetForm(); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Update' : 'New Progress Update'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Foundations complete!" data-testid="update-title" />
            </div>
            <div>
              <Label className="text-xs">Content</Label>
              <Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={6} placeholder="We are excited to share..." data-testid="update-content" />
            </div>

            <div>
              <Label className="text-xs">Image (optional)</Label>
              <div className="flex flex-col gap-2">
                {(imagePreview || form.image_url) && (
                  <div className="relative w-full max-w-[260px]">
                    <img
                      src={imagePreview || form.image_url}
                      alt="preview"
                      className="rounded border border-slate-200 max-h-44 object-cover w-full"
                      data-testid="update-image-preview"
                    />
                    <Button
                      variant="secondary" size="sm"
                      className="absolute top-1 right-1 h-7 w-7 p-0"
                      onClick={clearImage}
                      title="Remove image"
                      data-testid="update-image-clear"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={fileInputRef} type="file" accept="image/*"
                    onChange={onPickImage} className="hidden"
                    data-testid="update-image-input"
                  />
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="update-image-upload-btn"
                  >
                    {imageFile ? 'Change file' : 'Upload image'}
                  </Button>
                  {form.image_url && !imageFile && (
                    <span className="text-xs text-slate-400 truncate max-w-[220px]" data-testid="update-image-name">
                      {form.image_url.split('/').pop()}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400">JPG / PNG / GIF / WebP / SVG · max 5MB.</p>
              </div>
            </div>

            <div>
              <Label className="text-xs">Tags (comma-separated)</Label>
              <Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="milestone,phase-2" data-testid="update-tags" />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.is_published}
                onCheckedChange={(v) => setForm({ ...form, is_published: v })}
                data-testid="update-published-toggle"
              />
              <Label className="text-xs">Publish immediately (visible on public page)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAdd(false); setEditing(null); resetForm(); }}>Cancel</Button>
            <Button
              onClick={() => {
                if (!form.title.trim() || !form.content.trim()) { toast.error('Title and content required'); return; }
                if (editing) {
                  editMutation.mutate({
                    updateId: editing.id,
                    payload: {
                      title: form.title.trim(),
                      content: form.content.trim(),
                      // Only send image_url when not uploading a file. When user removes
                      // both file and url, send null to clear the image server-side.
                      image_url: imageFile ? undefined : (form.image_url.trim() || null),
                      tags: form.tags,
                      is_published: form.is_published,
                    },
                    file: imageFile,
                  });
                } else {
                  createMutation.mutate({ ...form, imageFile });
                }
              }}
              disabled={createMutation.isPending || editMutation.isPending}
              data-testid="update-submit"
            >
              {(createMutation.isPending || editMutation.isPending) ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Save' : 'Publish')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============== EMAIL BLAST TAB ==============
const TEMPLATE_OPTIONS = [
  { key: 'thank_you',        label: 'Thank You',        desc: 'Acknowledge donors who completed their pledge' },
  { key: 'pledge_reminder',  label: 'Pledge Reminder',  desc: 'Nudge donors with pending pledges' },
  { key: 'progress_update',  label: 'Progress Update',  desc: 'Share where the campaign stands today' },
  { key: 'welcome',          label: 'Welcome',          desc: 'Onboard new pledgers' },
  { key: 'payment_received', label: 'Payment Received', desc: 'Confirm a payment posted' },
];
const AUDIENCE_OPTIONS = [
  { key: 'all',       label: 'All Donors' },
  { key: 'completed', label: 'Donors Who Paid' },
  { key: 'pending',   label: 'Donors With Pending Pledges' },
  { key: 'failed',    label: 'Failed Payments' },
];

const EmailBlastTab = ({ campaignId, campaign }) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('compose'); // compose | log | settings
  const [template, setTemplate] = useState('progress_update');
  const [audience, setAudience] = useState('all');
  const [customSubject, setCustomSubject] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [contactEmail, setContactEmail] = useState(campaign?.contact_email || '');
  const [senderName, setSenderName] = useState(campaign?.sender_name || campaign?.name || '');

  const { data: log = [] } = useQuery({
    queryKey: ['campaign-email-log', campaignId],
    queryFn: () => api.get(`/campaigns/admin/${campaignId}/email/log?limit=30`).then(r => r.data),
  });

  // Get per-audience counts from the backend (which unions pledges +
  // donations + handles the status filter correctly). Avoids the old
  // client-side count that silently dropped pledges.
  const { data: counts = { all: 0, completed: 0, pending: 0, failed: 0, missing_email: 0, total_rows: 0 } } = useQuery({
    queryKey: ['campaign-email-counts', campaignId],
    queryFn: () => api.get(`/campaigns/admin/${campaignId}/email/audience-counts`).then(r => r.data),
  });
  const countFor = (key) => counts[key] || 0;

  const sendMutation = useMutation({
    mutationFn: (payload) => api.post(`/campaigns/admin/${campaignId}/email/bulk`, payload),
    onSuccess: (res) => {
      const d = res.data;
      if (d.email_configured) {
        toast.success(`Sent to ${d.sent_count}/${d.recipients_total} donors`);
      } else {
        toast.warning(`Logged for ${d.recipients_total} donors. Email service not configured.`);
      }
      queryClient.invalidateQueries({ queryKey: ['campaign-email-log', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-email-counts', campaignId] });
      setCustomSubject('');
      setCustomMessage('');
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Send failed'),
  });

  const saveContactMutation = useMutation({
    mutationFn: () => api.put(`/campaigns/admin/${campaignId}/contact-email`, {
      contact_email: contactEmail.trim(), sender_name: senderName.trim(),
    }),
    onSuccess: () => {
      toast.success('Linked email saved');
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Save failed'),
  });

  // ===== Donor Digest =====
  const [digestDays, setDigestDays] = useState(7);
  const [digestAudience, setDigestAudience] = useState('all');
  const [digestIntro, setDigestIntro] = useState('');
  const [digestSubject, setDigestSubject] = useState('');

  const digestMutation = useMutation({
    mutationFn: (payload) => api.post(`/campaigns/admin/${campaignId}/email/digest`, payload),
    onSuccess: (res) => {
      const d = res.data;
      if (d.email_configured) {
        toast.success(`Digest sent to ${d.sent_count}/${d.recipients_total} donors · ${d.updates_included} update${d.updates_included === 1 ? '' : 's'}`);
      } else {
        toast.warning(`Digest logged for ${d.recipients_total} donors (email not configured)`);
      }
      queryClient.invalidateQueries({ queryKey: ['campaign-email-log', campaignId] });
      setDigestIntro('');
      setDigestSubject('');
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Digest send failed'),
  });

  return (
    <div className="space-y-4 pt-2" data-testid="email-blast-tab">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2 sm:grid-cols-3">
          <TabsTrigger value="compose" data-testid="email-blast-tab-compose">Compose</TabsTrigger>
          <TabsTrigger value="log" data-testid="email-blast-tab-log">Send Log</TabsTrigger>
          <TabsTrigger value="settings" data-testid="email-blast-tab-settings">Linked Email</TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="mt-4 space-y-4">
          {/* Linked email banner */}
          {contactEmail ? (
            <div className="text-xs text-slate-500 bg-slate-50 border rounded px-3 py-2">
              Sending from <strong className="text-slate-700">{senderName || campaign?.name} &lt;{contactEmail}&gt;</strong>
            </div>
          ) : (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              No campaign-linked email set yet. <button className="underline" onClick={() => setTab('settings')}>Set it here →</button> Emails will fall back to the platform default.
            </div>
          )}

          {/* Template picker */}
          <div>
            <Label className="text-xs">Template</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
              {TEMPLATE_OPTIONS.map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTemplate(t.key)}
                  className={`text-left p-3 rounded-lg border transition-all ${
                    template === t.key
                      ? 'border-emerald-400 bg-emerald-50/50 ring-1 ring-emerald-100'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`email-template-${t.key}`}
                >
                  <p className="text-sm font-medium text-slate-800">{t.label}</p>
                  <p className="text-xs text-slate-500">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Audience picker */}
          <div>
            <Label className="text-xs">Audience</Label>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              className="w-full h-10 border rounded-md px-3 text-sm mt-1"
              data-testid="email-audience"
            >
              {AUDIENCE_OPTIONS.map(a => (
                <option key={a.key} value={a.key}>{a.label} — {countFor(a.key)} recipient{countFor(a.key) === 1 ? '' : 's'}</option>
              ))}
            </select>
          </div>

          {/* Optional custom subject + message */}
          <div>
            <Label className="text-xs">Custom subject <span className="text-slate-400">(optional — overrides template)</span></Label>
            <Input
              value={customSubject}
              onChange={(e) => setCustomSubject(e.target.value)}
              placeholder="Leave blank to use the template's subject"
              data-testid="email-custom-subject"
            />
          </div>
          <div>
            <Label className="text-xs">Custom message <span className="text-slate-400">(optional — fills {`{{message}}`} placeholder in the template)</span></Label>
            <Textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={4}
              placeholder="e.g. We just completed foundations and are moving into the structural phase..."
              data-testid="email-custom-message"
            />
          </div>

          <div className="pt-2 flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              About to email <strong>{countFor(audience)}</strong> unique recipient{countFor(audience) === 1 ? '' : 's'}.
              {counts.missing_email > 0 && (
                <span className="block text-amber-700 mt-1" data-testid="missing-email-warning">
                  ⚠ {counts.missing_email} donor{counts.missing_email === 1 ? '' : 's'} in this campaign {counts.missing_email === 1 ? 'has' : 'have'} no email on file — they won't be reached. Add their email in the Donors tab to include them.
                </span>
              )}
            </div>
            <Button
              onClick={() => {
                if (countFor(audience) === 0) { toast.error('No recipients in that audience'); return; }
                if (!window.confirm(`Send "${TEMPLATE_OPTIONS.find(t => t.key === template)?.label}" to ${countFor(audience)} donors?`)) return;
                sendMutation.mutate({
                  template_key: template,
                  audience,
                  custom_subject: customSubject.trim() || null,
                  custom_message: customMessage.trim() || null,
                });
              }}
              disabled={sendMutation.isPending}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="email-send-btn"
            >
              {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send to {countFor(audience)} donor{countFor(audience) === 1 ? '' : 's'}
            </Button>
          </div>

          {/* ===== DONOR DIGEST ===== */}
          <div className="mt-8 pt-6 border-t-2 border-dashed border-emerald-200" data-testid="donor-digest-panel">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                <MessageSquare className="h-4 w-4 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-slate-800">Donor Digest · Auto-batch recent updates</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Bundle the last few days of <strong>published</strong> updates into a single
                  &quot;here&apos;s what your donation built&quot; email. Great for weekly/monthly newsletters.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
              <div>
                <Label className="text-xs">Window</Label>
                <select
                  value={digestDays}
                  onChange={(e) => setDigestDays(parseInt(e.target.value, 10))}
                  className="w-full h-9 border rounded-md px-2 text-sm"
                  data-testid="digest-days"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={60}>Last 60 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Audience</Label>
                <select
                  value={digestAudience}
                  onChange={(e) => setDigestAudience(e.target.value)}
                  className="w-full h-9 border rounded-md px-2 text-sm"
                  data-testid="digest-audience"
                >
                  <option value="all">All Donors — {countFor('all')}</option>
                  <option value="completed">Donors who paid — {countFor('completed')}</option>
                  <option value="pending">Pending pledgers — {countFor('pending')}</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Subject (optional)</Label>
                <Input
                  value={digestSubject}
                  onChange={(e) => setDigestSubject(e.target.value)}
                  placeholder="Auto-generated if blank"
                  className="h-9 text-sm"
                  data-testid="digest-subject"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Intro message (optional)</Label>
              <Textarea
                value={digestIntro}
                onChange={(e) => setDigestIntro(e.target.value)}
                rows={2}
                placeholder="Add a personal note above the bundled updates..."
                data-testid="digest-intro"
              />
            </div>
            <div className="flex items-center justify-end mt-3">
              <Button
                onClick={() => {
                  if (countFor(digestAudience) === 0) { toast.error('No recipients in that audience'); return; }
                  if (!window.confirm(`Send a digest of the last ${digestDays} days to ${countFor(digestAudience)} donor${countFor(digestAudience) === 1 ? '' : 's'}?`)) return;
                  digestMutation.mutate({
                    days: digestDays,
                    audience: digestAudience,
                    only_published: true,
                    intro_message: digestIntro.trim() || null,
                    custom_subject: digestSubject.trim() || null,
                  });
                }}
                disabled={digestMutation.isPending}
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="digest-send-btn"
              >
                {digestMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send digest to {countFor(digestAudience)} donor{countFor(digestAudience) === 1 ? '' : 's'}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="log" className="mt-4">
          {log.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">
              <Mail className="h-6 w-6 mx-auto mb-2 opacity-50" />
              No emails sent yet. Compose your first blast in the Compose tab.
            </div>
          ) : (
            <div className="divide-y border rounded-lg">
              {log.map(row => (
                <div key={row.id} className="p-3 text-sm flex items-center gap-3" data-testid={`email-log-${row.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{TEMPLATE_OPTIONS.find(t => t.key === row.template_key)?.label || row.template_key}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100">{row.audience}</span>
                      {!row.email_configured && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Logged only</span>}
                    </div>
                    <p className="text-xs text-slate-500">
                      {row.sent_count}/{row.recipient_count} sent · by {row.sent_by_email || '—'} · {row.sent_at ? new Date(row.sent_at).toLocaleString() : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="settings" className="mt-4 space-y-4">
          <p className="text-sm text-slate-500">
            Set the email address donors will see as the sender. We use this as the Reply-To and (where SMTP allows) the From identity.
          </p>
          <div>
            <Label className="text-xs">Sender name</Label>
            <Input
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder={campaign?.name || 'Campaign team'}
              data-testid="campaign-sender-name"
            />
          </div>
          <div>
            <Label className="text-xs">Linked email address</Label>
            <Input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="campaign@yourdomain.com"
              data-testid="campaign-contact-email"
            />
            <p className="text-[11px] text-slate-400 mt-1">Donors reply here. Falls back to platform default if blank.</p>
          </div>
          <Button onClick={() => saveContactMutation.mutate()} disabled={saveContactMutation.isPending} data-testid="campaign-save-linked-email">
            {saveContactMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Linked Email
          </Button>
        </TabsContent>
      </Tabs>
    </div>
  );
};


// ============== WHATSAPP BLAST TAB (uses central OpenWA engine) ==============
const WA_TEMPLATE_OPTIONS = [
  { key: 'campaign_donation_confirmation', label: 'Thank-you', desc: 'Thanks for the gift' },
  { key: 'campaign_pledge_reminder',       label: 'Pledge Reminder', desc: 'Nudge outstanding pledges' },
  { key: 'announcement_broadcast',         label: 'Announcement', desc: 'Generic title + body' },
  { key: 'donation_thank_you',             label: 'Generic Thank-you', desc: 'Tenant-wide donation thanks' },
];

const WhatsAppBlastTab = ({ campaignId, campaign }) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('compose');
  const [template, setTemplate] = useState('campaign_donation_confirmation');
  const [audience, setAudience] = useState('all');
  const [customMessage, setCustomMessage] = useState('');

  // Donor digest controls
  const [digestDays, setDigestDays] = useState(7);
  const [digestAudience, setDigestAudience] = useState('all');
  const [digestIntro, setDigestIntro] = useState('');

  const { data: counts = { all: 0, completed: 0, pending: 0, failed: 0, missing_phone: 0, total_rows: 0 } } = useQuery({
    queryKey: ['campaign-wa-counts', campaignId],
    queryFn: () => api.get(`/campaigns/admin/${campaignId}/whatsapp/audience-counts`).then(r => r.data),
  });
  const countFor = (k) => counts[k] || 0;

  const { data: log = [] } = useQuery({
    queryKey: ['campaign-wa-log', campaignId],
    queryFn: () => api.get(`/campaigns/admin/${campaignId}/whatsapp/log?limit=30`).then(r => r.data),
  });

  const sendMutation = useMutation({
    mutationFn: (payload) => api.post(`/campaigns/admin/${campaignId}/whatsapp/bulk`, payload),
    onSuccess: (res) => {
      const d = res.data;
      toast.success(`${d.sent_count}/${d.recipients_total} queued · ${d.skipped_count} opt-outs · ${d.failed_count} failed`);
      queryClient.invalidateQueries({ queryKey: ['campaign-wa-counts', campaignId] });
      queryClient.invalidateQueries({ queryKey: ['campaign-wa-log', campaignId] });
      setCustomMessage('');
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'WhatsApp send failed'),
  });

  const digestMutation = useMutation({
    mutationFn: (payload) => api.post(`/campaigns/admin/${campaignId}/whatsapp/digest`, payload),
    onSuccess: (res) => {
      const d = res.data;
      toast.success(
        `Digest sent to ${d.sent_count}/${d.recipients_total} · ${d.updates_included} update${d.updates_included === 1 ? '' : 's'} bundled`
      );
      queryClient.invalidateQueries({ queryKey: ['campaign-wa-log', campaignId] });
      setDigestIntro('');
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Digest send failed'),
  });

  return (
    <div className="space-y-4 pt-2" data-testid="campaign-whatsapp-blast-tab">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="compose" data-testid="wa-blast-tab-compose">Compose</TabsTrigger>
          <TabsTrigger value="log" data-testid="wa-blast-tab-log">Send Log</TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="mt-4 space-y-4">
          <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
            Sending through your platform OpenWA channel. Recipients who replied <strong>STOP</strong> are auto-skipped.
            Configure or check status at <a href="/admin/whatsapp" target="_blank" rel="noreferrer" className="underline">/admin/whatsapp</a>.
          </div>

          <div>
            <Label className="text-xs">Template</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
              {WA_TEMPLATE_OPTIONS.map(t => (
                <button
                  key={t.key} type="button"
                  onClick={() => setTemplate(t.key)}
                  className={`text-left p-3 rounded-lg border transition-all ${
                    template === t.key ? 'border-emerald-400 bg-emerald-50/50 ring-1 ring-emerald-100' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`wa-template-${t.key}`}
                >
                  <p className="text-sm font-medium text-slate-800">{t.label}</p>
                  <p className="text-xs text-slate-500">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Audience</Label>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              className="w-full h-10 border rounded-md px-3 text-sm mt-1"
              data-testid="wa-audience"
            >
              <option value="all">All Donors — {countFor('all')} recipients</option>
              <option value="completed">Donors Who Paid — {countFor('completed')}</option>
              <option value="pending">Donors With Pending Pledges — {countFor('pending')}</option>
              <option value="failed">Failed Payments — {countFor('failed')}</option>
            </select>
          </div>

          <div>
            <Label className="text-xs">Custom message <span className="text-slate-400">(optional — when set, sent as plain text instead of the template)</span></Label>
            <Textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={4}
              placeholder="e.g. Praise God, our roof fund just hit 80%! Thank you for being part of this..."
              data-testid="wa-custom-message"
            />
          </div>

          <div className="pt-2 flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              About to message <strong>{countFor(audience)}</strong> donor{countFor(audience) === 1 ? '' : 's'} on WhatsApp.
              {counts.missing_phone > 0 && (
                <span className="block text-amber-700 mt-1" data-testid="wa-missing-phone-warning">
                  ⚠ {counts.missing_phone} donor{counts.missing_phone === 1 ? '' : 's'} {counts.missing_phone === 1 ? 'has' : 'have'} no phone on file — they won't be reached.
                </span>
              )}
            </div>
            <Button
              onClick={() => {
                if (countFor(audience) === 0) { toast.error('No recipients with phone numbers in that audience'); return; }
                if (!window.confirm(`Send WhatsApp to ${countFor(audience)} donor${countFor(audience) === 1 ? '' : 's'}?`)) return;
                sendMutation.mutate({
                  template_key: template,
                  audience,
                  custom_message: customMessage.trim() || null,
                });
              }}
              disabled={sendMutation.isPending}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="wa-send-btn"
            >
              {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send to {countFor(audience)} donor{countFor(audience) === 1 ? '' : 's'}
            </Button>
          </div>

          {/* Donor Digest - bundle last N days of updates into one WA message */}
          <div className="mt-6 pt-4 border-t" data-testid="wa-digest-block">
            <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-emerald-600" /> Donor Digest
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Auto-bundle the last N days of campaign updates into ONE WhatsApp message — perfect for weekly check-ins.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Lookback days</Label>
                <Input
                  type="number" min="1" max="90"
                  value={digestDays}
                  onChange={(e) => setDigestDays(Number(e.target.value) || 7)}
                  data-testid="wa-digest-days"
                />
              </div>
              <div>
                <Label className="text-xs">Audience</Label>
                <select
                  value={digestAudience}
                  onChange={(e) => setDigestAudience(e.target.value)}
                  className="w-full h-10 border rounded-md px-3 text-sm"
                  data-testid="wa-digest-audience"
                >
                  <option value="all">All Donors — {countFor('all')}</option>
                  <option value="completed">Donors Who Paid — {countFor('completed')}</option>
                  <option value="pending">Pending Pledges — {countFor('pending')}</option>
                </select>
              </div>
              <div className="md:col-span-1 flex items-end">
                <Button
                  onClick={() => {
                    if (countFor(digestAudience) === 0) { toast.error('No recipients with phone numbers'); return; }
                    if (!window.confirm(`Bundle last ${digestDays} days of updates and send to ${countFor(digestAudience)} donor${countFor(digestAudience) === 1 ? '' : 's'} on WhatsApp?`)) return;
                    digestMutation.mutate({
                      days: digestDays,
                      audience: digestAudience,
                      intro_message: digestIntro.trim() || null,
                    });
                  }}
                  disabled={digestMutation.isPending}
                  variant="outline"
                  className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="wa-digest-send-btn"
                >
                  {digestMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  Send digest
                </Button>
              </div>
            </div>
            <div className="mt-2">
              <Label className="text-xs">Intro message <span className="text-slate-400">(optional — prepended to the digest)</span></Label>
              <Textarea
                value={digestIntro}
                onChange={(e) => setDigestIntro(e.target.value)}
                rows={2}
                placeholder="e.g. Praise God family — here's a quick recap of what your gifts built this week..."
                data-testid="wa-digest-intro"
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="log" className="mt-4">
          {log.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">
              <MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-50" />
              No WhatsApp blasts sent yet.
            </div>
          ) : (
            <div className="divide-y border rounded-lg">
              {log.map(row => (
                <div key={row.id} className="p-3 text-sm" data-testid={`wa-log-${row.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{WA_TEMPLATE_OPTIONS.find(t => t.key === row.template_key)?.label || row.template_key}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100">{row.audience}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {row.sent_count}/{row.recipient_count} queued · {row.skipped_count || 0} opt-outs · {row.failed_count || 0} failed · by {row.sent_by_email || '—'} · {row.sent_at ? new Date(row.sent_at).toLocaleString() : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};


// ============== CAMPAIGN TEAM TAB ==============
const ROLE_META = {
  campaign_admin:   { label: 'Campaign Admin',   color: 'bg-violet-100 text-violet-700',   desc: 'Full control: settings, payments, team & comms' },
  campaign_manager: { label: 'Manager',          color: 'bg-blue-100 text-blue-700',       desc: 'Manage donors/pledges, send notifications' },
  donation_chaser:  { label: 'Donation Chaser',  color: 'bg-emerald-100 text-emerald-700', desc: 'View & chase donors, mark pledges paid, send reminders' },
  viewer:           { label: 'Viewer',           color: 'bg-slate-100 text-slate-700',     desc: 'Read-only access' },
};

const CampaignTeamTab = ({ campaignId }) => {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'donation_chaser' });

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['campaign-team', campaignId],
    queryFn: () => api.get(`/campaigns/${campaignId}/team`).then(r => r.data),
  });

  const addMutation = useMutation({
    mutationFn: (payload) => api.post(`/campaigns/${campaignId}/team`, payload),
    onSuccess: (resp) => {
      const r = resp?.data || {};
      toast.success(
        r.created_user
          ? `Team member added · invite email sent to ${r.email}`
          : `Team member added · sign-in link sent to ${r.email}`
      );
      queryClient.invalidateQueries({ queryKey: ['campaign-team', campaignId] });
      setShowAdd(false);
      setForm({ email: '', name: '', password: '', role: 'donation_chaser' });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to add member'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ memberId, payload }) => api.put(`/campaigns/${campaignId}/team/${memberId}`, payload),
    onSuccess: () => {
      toast.success('Member updated');
      queryClient.invalidateQueries({ queryKey: ['campaign-team', campaignId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to update'),
  });

  const removeMutation = useMutation({
    mutationFn: ({ memberId, hard }) => api.delete(`/campaigns/${campaignId}/team/${memberId}${hard ? '?hard=true' : ''}`),
    onSuccess: () => {
      toast.success('Member removed');
      queryClient.invalidateQueries({ queryKey: ['campaign-team', campaignId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to remove'),
  });

  const resendMutation = useMutation({
    mutationFn: ({ memberId, reset_password }) => api.post(`/campaigns/${campaignId}/team/${memberId}/resend-invite`, { reset_password: !!reset_password }),
    onSuccess: (resp) => {
      toast.success(resp?.data?.password_reset ? 'Invite re-sent · password reset' : 'Invite re-sent');
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed to resend invite'),
  });

  const handleAdd = () => {
    if (!form.email || !form.role) {
      toast.error('Email and role are required');
      return;
    }
    addMutation.mutate({
      email: form.email.trim().toLowerCase(),
      name: form.name.trim() || undefined,
      password: form.password || undefined,
      role: form.role,
    });
  };

  if (isLoading) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>;

  return (
    <div className="space-y-4 pt-4" data-testid="campaign-team-tab">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-slate-500">
          Scope team members to this campaign only. Donation chasers can track and follow up with donors without seeing other campaigns.
        </p>
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid="campaign-add-member-btn">
          <UserPlus className="h-3.5 w-3.5 mr-1" /> Add Member
        </Button>
      </div>

      {/* Member list */}
      <div className="space-y-2">
        {members.length === 0 && (
          <div className="text-center py-8 border border-dashed rounded-lg text-slate-400 text-sm">
            <Users className="h-6 w-6 mx-auto mb-2 opacity-50" />
            No team members yet. Add a chaser to start tracking pledges.
          </div>
        )}
        {members.map(m => {
          const meta = ROLE_META[m.role] || ROLE_META.viewer;
          return (
            <Card key={m.id} className={m.is_active ? '' : 'opacity-60'} data-testid={`campaign-team-row-${m.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                  <Shield className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{m.name || m.email}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
                    {!m.is_active && <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Disabled</span>}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{m.email}</p>
                </div>
                <select
                  value={m.role}
                  onChange={(e) => updateMutation.mutate({ memberId: m.id, payload: { role: e.target.value } })}
                  className="text-xs border rounded px-2 py-1"
                  data-testid={`campaign-team-role-${m.id}`}
                >
                  {Object.keys(ROLE_META).map(r => (
                    <option key={r} value={r}>{ROLE_META[r].label}</option>
                  ))}
                </select>
                <Switch
                  checked={!!m.is_active}
                  onCheckedChange={(v) => updateMutation.mutate({ memberId: m.id, payload: { is_active: v } })}
                  data-testid={`campaign-team-toggle-${m.id}`}
                />
                <Button
                  variant="ghost" size="sm"
                  onClick={() => {
                    const reset = window.confirm(`Re-send invite email to ${m.email}?\n\nClick OK to also reset their password (recommended if they never logged in).\nClick Cancel to just resend without resetting.`);
                    resendMutation.mutate({ memberId: m.id, reset_password: reset });
                  }}
                  className="h-8 text-xs text-emerald-700 hover:text-emerald-900"
                  title="Re-send invite email"
                  data-testid={`campaign-team-resend-${m.id}`}
                >
                  Resend
                </Button>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => {
                    if (window.confirm(`Permanently remove ${m.email} from this campaign?`)) {
                      removeMutation.mutate({ memberId: m.id, hard: true });
                    }
                  }}
                  className="h-8 w-8 p-0 text-red-400 hover:text-red-600"
                  data-testid={`campaign-team-delete-${m.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Role legend */}
      <div className="text-xs text-slate-500 space-y-1 pt-2 border-t">
        {Object.entries(ROLE_META).map(([r, meta]) => (
          <div key={r}>
            <span className={`inline-block px-1.5 py-0.5 rounded mr-1 ${meta.color}`}>{meta.label}</span>
            <span className="text-slate-400">{meta.desc}</span>
          </div>
        ))}
      </div>

      {/* Add dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Campaign Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="member@example.com" data-testid="campaign-add-email" />
            </div>
            <div>
              <Label className="text-xs">Name (optional)</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <select
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}
                className="w-full h-10 border rounded-md px-3 text-sm"
                data-testid="campaign-add-role"
              >
                {Object.keys(ROLE_META).map(r => (
                  <option key={r} value={r}>{ROLE_META[r].label} — {ROLE_META[r].desc}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Password <span className="text-slate-400">(only needed if user doesn't already exist)</span></Label>
              <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Temporary password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={addMutation.isPending} data-testid="campaign-add-submit">
              {addMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Member'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignSettings;